// Shared dealership geo helper for ad feeds (Meta AIA, Google Shopping, etc.)
// ---------------------------------------------------------------------------
// Resolution order:
//   1. In-memory override cache (populated from `site_settings.dealer_geo_override`
//      by the server-only loader in `dealer-geo.server.ts`).
//   2. CONTACT.address.latitude/longitude (compile-time defaults / seed values).
//
// API stays SYNCHRONOUS so SSR JSON-LD builders (`jsonld.ts`) and feed
// generators can call it inside route head() without awaiting. The async DB
// fetch happens out-of-band: at server boot (`start.ts`) and whenever the
// admin saves a new value (the save-handler updates the cache directly).
import { CONTACT } from "@/config/contact";

export interface DealerGeo {
  latitude: number;
  longitude: number;
}

/** Stable identifier used as `dealer_id` across feeds. Update with caution —
 *  Meta/Google use this to merge inventory with existing dealer records. */
export const DEALER_ID = "autoxchange-borkop";

/** site_settings key holding the admin-editable override. */
export const DEALER_GEO_SETTINGS_KEY = "dealer_geo_override";

/** Denmark's geographic bounding box (approx). Catches typos like swapped
 *  lat/lng or pasting Google-Maps output from the wrong country. */
export const DENMARK_BBOX = {
  latMin: 54.5,
  latMax: 57.8,
  lngMin: 8.0,
  lngMax: 15.2,
} as const;

// ---------------------------------------------------------------------------
// Override cache (server-side, populated by dealer-geo.server.ts on boot
// and by the admin save server-function). On the client the cache is empty,
// which is fine — client code never renders feeds and CONTACT defaults
// give a correct UI fallback for JSON-LD until SSR replaces it.
// ---------------------------------------------------------------------------
let _override: DealerGeo | null = null;
let _overrideLoaded = false;

/** Set the in-memory override. Pass `null` to clear (revert to CONTACT). */
export function setDealerGeoOverride(value: DealerGeo | null): void {
  _override = value;
  _overrideLoaded = true;
}

/** True once the loader (DB or admin save) has touched the cache at least
 *  once this process. Used by diagnostics to show "loaded from DB" state. */
export function isDealerGeoOverrideLoaded(): boolean {
  return _overrideLoaded;
}

/** Returns the active override or null. Does not fall back to CONTACT. */
export function getDealerGeoOverride(): DealerGeo | null {
  return _override;
}

// ---------------------------------------------------------------------------
// Validation helpers (pure / side-effect-free).
// ---------------------------------------------------------------------------
function isValidLat(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && n >= -90 && n <= 90 && n !== 0;
}

function isValidLng(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && n >= -180 && n <= 180 && n !== 0;
}

/**
 * Returns the dealership's latitude/longitude when both are valid finite
 * numbers within range, otherwise `null`. Treats `0,0` as invalid because
 * it is almost always an "unset" sentinel rather than a real location.
 *
 * Resolution: override → CONTACT.
 */
export function getDealerGeo(): DealerGeo | null {
  if (_override && isValidLat(_override.latitude) && isValidLng(_override.longitude)) {
    return _override;
  }
  const lat = CONTACT.address.latitude;
  const lng = CONTACT.address.longitude;
  if (isValidLat(lat) && isValidLng(lng)) {
    return { latitude: lat, longitude: lng };
  }
  return null;
}

/** Convenience: just the latitude, or null. */
export function getDealerLatitude(): number | null {
  return getDealerGeo()?.latitude ?? null;
}

/** Convenience: just the longitude, or null. */
export function getDealerLongitude(): number | null {
  return getDealerGeo()?.longitude ?? null;
}

/**
 * Format coordinates for feeds that expect a single "lat,lng" string
 * (e.g. some Google Shopping local-inventory extensions). Returns null
 * when either coordinate is missing/invalid.
 */
export function formatDealerGeoString(precision = 6): string | null {
  const geo = getDealerGeo();
  if (!geo) return null;
  return `${geo.latitude.toFixed(precision)},${geo.longitude.toFixed(precision)}`;
}

export interface DealerGeoValidation {
  ok: boolean;
  /** Human-readable reasons why the coordinates were rejected. Empty when ok. */
  issues: string[];
  latitude: unknown;
  longitude: unknown;
}

/**
 * Validate a candidate (latitude, longitude) pair. Used both at startup
 * (to check CONTACT defaults) and at save-time in the admin editor.
 *
 * Rules enforced:
 *   - Both numeric + finite
 *   - Latitude ∈ [-90, 90], Longitude ∈ [-180, 180]
 *   - 0,0 sentinel rejected
 *   - Inside Denmark bounding box (catches swapped lat/lng)
 *   - Both required together (cannot save lat without lng or vice-versa)
 */
export function validateDealerGeoCandidate(
  lat: unknown,
  lng: unknown,
): DealerGeoValidation {
  const issues: string[] = [];
  const latPresent = lat !== null && lat !== undefined && lat !== "";
  const lngPresent = lng !== null && lng !== undefined && lng !== "";

  if (latPresent !== lngPresent) {
    issues.push(
      "latitude og longitude skal udfyldes sammen — kan ikke gemmes hver for sig",
    );
  }

  if (latPresent) {
    if (typeof lat !== "number" || !Number.isFinite(lat)) {
      issues.push(`latitude er ikke et endeligt tal (fik ${JSON.stringify(lat)})`);
    } else if (lat < -90 || lat > 90) {
      issues.push(`latitude ${lat} er uden for [-90..90]`);
    } else if (lat === 0) {
      issues.push("latitude er 0 — ligner et tomt sentinel-værdi");
    } else if (lat < DENMARK_BBOX.latMin || lat > DENMARK_BBOX.latMax) {
      issues.push(
        `latitude ${lat} er uden for Danmarks bbox (${DENMARK_BBOX.latMin}..${DENMARK_BBOX.latMax}) — har du byttet om på lat/lng?`,
      );
    }
  }

  if (lngPresent) {
    if (typeof lng !== "number" || !Number.isFinite(lng)) {
      issues.push(`longitude er ikke et endeligt tal (fik ${JSON.stringify(lng)})`);
    } else if (lng < -180 || lng > 180) {
      issues.push(`longitude ${lng} er uden for [-180..180]`);
    } else if (lng === 0) {
      issues.push("longitude er 0 — ligner et tomt sentinel-værdi");
    } else if (lng < DENMARK_BBOX.lngMin || lng > DENMARK_BBOX.lngMax) {
      issues.push(
        `longitude ${lng} er uden for Danmarks bbox (${DENMARK_BBOX.lngMin}..${DENMARK_BBOX.lngMax}) — har du byttet om på lat/lng?`,
      );
    }
  }

  return { ok: issues.length === 0, issues, latitude: lat, longitude: lng };
}

/**
 * Inspect the *currently active* coordinates (override → CONTACT) and
 * return a structured report. Skips the Denmark bbox check so legacy
 * CONTACT values are not flagged after the bbox tightening.
 */
export function validateDealerGeo(): DealerGeoValidation {
  const active = getDealerGeo();
  const lat = active?.latitude ?? CONTACT.address.latitude;
  const lng = active?.longitude ?? CONTACT.address.longitude;
  const issues: string[] = [];

  if (lat === null || lat === undefined) {
    issues.push("latitude is missing");
  } else if (typeof lat !== "number" || !Number.isFinite(lat)) {
    issues.push(`latitude is not a finite number (got ${JSON.stringify(lat)})`);
  } else if (lat < -90 || lat > 90) {
    issues.push(`latitude ${lat} is out of bounds (-90..90)`);
  } else if (lat === 0) {
    issues.push("latitude is 0 — looks like an unset sentinel");
  }

  if (lng === null || lng === undefined) {
    issues.push("longitude is missing");
  } else if (typeof lng !== "number" || !Number.isFinite(lng)) {
    issues.push(`longitude is not a finite number (got ${JSON.stringify(lng)})`);
  } else if (lng < -180 || lng > 180) {
    issues.push(`longitude ${lng} is out of bounds (-180..180)`);
  } else if (lng === 0) {
    issues.push("longitude is 0 — looks like an unset sentinel");
  }

  return { ok: issues.length === 0, issues, latitude: lat, longitude: lng };
}

/**
 * Run validation once and emit a single console.warn line if anything is
 * wrong. Idempotent — subsequent calls within the same process are no-ops
 * so we don't spam logs on hot reload or repeated SSR worker boots.
 */
let _logged = false;
export function logDealerGeoStartupWarning(): DealerGeoValidation {
  const result = validateDealerGeo();
  if (!_logged && !result.ok) {
    _logged = true;
    console.warn(
      `[dealer-geo] CONTACT.address coordinates invalid — ad feeds (Meta AIA, Google Shopping) will omit <latitude>/<longitude> and lose distance-based targeting. Issues: ${result.issues.join("; ")}`,
    );
  }
  return result;
}

/** Test-only: reset all module state so each test starts clean. */
export function __resetDealerGeoWarningForTests(): void {
  _logged = false;
  _override = null;
  _overrideLoaded = false;
}
